import axios from "axios";
import { API_URL } from "../utils/api-config";

// Configurer axios avec des options par défaut
axios.defaults.headers.common["Content-Type"] = "application/json";

// Types
// Type pour l'équipement de manutention
export interface AssignmentEquipment {
  taken: boolean;
  returned: boolean;
  equipmentId?: string;
  equipmentType?: string;
  equipmentNumber?: string;
  equipmentBrand?: string;
  equipmentModel?: string;
  returnComment?: string;
  returnCondition?: "bon_etat" | "endommagé" | "non_rendu";
}

export interface Assignment {
  _id: string;
  vehiclePlate: string;
  driverId: string;
  driverFirstName: string;
  driverLastName: string;
  date: string;
  time: string;
  status: "en_cours" | "terminée";
  currentStep: string;
  purpose: string;
  startMileage: number;
  startFuelLevel: number;
  endDate?: string;
  endTime?: string;
  endMileage?: number;
  endFuelLevel?: string;
  comments?: string;
  initialState?: VehicleState;
  finalState?: VehicleState;
  signatures?: Signatures;
  returnSignatures?: Signatures;
  equipment?: AssignmentEquipment; // Équipement de manutention optionnel
  createdBy?: string;
  createdByName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleState {
  cleanliness: {
    interior: boolean;
    exterior: boolean;
    cabine?: boolean;
    comments?: string;
    photos?: string[];
  };
  checklist: {
    lights: "conforme" | "non conforme";
    brakes: "conforme" | "non conforme";
    tires: "conforme" | "non conforme";
    documents: "conforme" | "non conforme";
    comments?: string;
  };
  degradations: {
    count: number;
    details: {
      rayures: number;
      chocs: number;
      manquant: number;
      casse: number;
    };
    images?: {
      url: string;
      damageType: string;
      face: string;
    }[];
  };
}

export interface Signatures {
  manager?: string;
  conducteur?: string;
}

export interface SignaturesData {
  signatures: Signatures;
}

export interface ChecklistData {
  lights: "conforme" | "non conforme";
  brakes: "conforme" | "non conforme";
  tires: "conforme" | "non conforme";
  documents: "conforme" | "non conforme";
  comments?: string;
  isReturn?: boolean;
}

export interface EndAssignmentData {
  endDate: string;
  endTime: string;
  endMileage: number;
  endFuelLevel: string;
  comments?: string;
}

export interface CreateAssignmentDto {
  vehiclePlate: string;
  driverId: string;
  driverFirstName: string;
  driverLastName: string;
  date: string;
  time: string;
  startMileage: number;
  startFuelLevel: number;
  purpose: string;
}

// Fonction utilitaire pour logger et gérer les erreurs
const handleApiError = (context: string, error: unknown) => {
  if (axios.isAxiosError(error)) {
    console.error(`${context}:`, error.message);
    if (error.response) {
      console.error(`Statut: ${error.response.status}`);
      console.error(`Données: ${JSON.stringify(error.response.data)}`);
    }
  } else {
    console.error(`${context}:`, error);
  }
  throw error;
};

// Service pour gérer les affectations
const AssignmentService = {
  // Récupérer toutes les affectations
  getAll: async (): Promise<Assignment[]> => {
    try {
      console.log("Requête GET vers", `${API_URL}/assignments`);
      const response = await axios.get(`${API_URL}/assignments`);
      console.log("Réponse reçue:", response.status);
      return response.data;
    } catch (error) {
      return handleApiError(
        "Erreur lors de la récupération des affectations",
        error
      );
    }
  },

  // Récupérer une affectation par ID
  getById: async (id: string): Promise<Assignment> => {
    try {
      console.log("Requête GET vers", `${API_URL}/assignments/${id}`);
      const response = await axios.get(`${API_URL}/assignments/${id}`);
      console.log("Réponse reçue:", response.status);
      return response.data;
    } catch (error) {
      return handleApiError(
        "Erreur lors de la récupération de l'affectation",
        error
      );
    }
  },

  // Créer une nouvelle affectation
  create: async (assignmentData: CreateAssignmentDto): Promise<Assignment> => {
    try {
      console.log("Requête POST vers", `${API_URL}/assignments`);
      console.log("Données envoyées:", JSON.stringify(assignmentData));
      const response = await axios.post(
        `${API_URL}/assignments`,
        assignmentData
      );
      console.log("Réponse reçue:", response.status);
      return response.data;
    } catch (error) {
      return handleApiError(
        "Erreur lors de la création de l'affectation",
        error
      );
    }
  },

  // Supprimer une affectation
  delete: async (id: string): Promise<void> => {
    try {
      console.log("Requête DELETE vers", `${API_URL}/assignments/${id}`);
      const response = await axios.delete(`${API_URL}/assignments/${id}`);
      console.log("Réponse reçue:", response.status);
    } catch (error) {
      handleApiError("Erreur lors de la suppression de l'affectation", error);
    }
  },

  // Passer à l'étape suivante
  nextStep: async (id: string): Promise<Assignment> => {
    try {
      console.log(
        "[AssignmentService.nextStep] Requête PATCH vers",
        `${API_URL}/assignments/${id}/next-step`
      );
      const response = await axios.patch(
        `${API_URL}/assignments/${id}/next-step`,
        {}
      );
      console.log(
        "[AssignmentService.nextStep] Réponse reçue:",
        response.status
      );
      console.log(
        "[AssignmentService.nextStep] Données reçues:",
        JSON.stringify(response.data)
      );
      return response.data.assignment || response.data;
    } catch (error) {
      console.error(
        "[AssignmentService.nextStep] Erreur lors du passage à l'étape suivante:",
        error
      );
      return handleApiError("Erreur lors du passage à l'étape suivante", error);
    }
  },

  // Revenir à l'étape précédente
  previousStep: async (id: string): Promise<Assignment> => {
    try {
      console.log(
        "[AssignmentService.previousStep] Requête PATCH vers",
        `${API_URL}/assignments/${id}/previous-step`
      );
      const response = await axios.patch(
        `${API_URL}/assignments/${id}/previous-step`,
        {}
      );
      console.log(
        "[AssignmentService.previousStep] Réponse reçue:",
        response.status
      );
      console.log(
        "[AssignmentService.previousStep] Données reçues:",
        JSON.stringify(response.data)
      );
      return response.data.assignment || response.data;
    } catch (error) {
      console.error(
        "[AssignmentService.previousStep] Erreur lors du retour à l'étape précédente:",
        error
      );
      return handleApiError(
        "Erreur lors du retour à l'étape précédente",
        error
      );
    }
  },

  // Ajouter des dégradations
  addDegradations: async (
    id: string,
    degradationsData: VehicleState["degradations"],
    isReturn = false
  ): Promise<Assignment> => {
    try {
      const url = `${API_URL}/assignments/${id}/degradations${
        isReturn ? "?return=true" : ""
      }`;
      console.log(
        "[AssignmentService.addDegradations] Requête PATCH vers",
        url
      );
      console.log(
        "[AssignmentService.addDegradations] Données envoyées:",
        JSON.stringify(degradationsData)
      );
      console.log("[AssignmentService.addDegradations] Mode retour:", isReturn);

      const response = await axios.patch(url, degradationsData);

      console.log(
        "[AssignmentService.addDegradations] Réponse reçue:",
        response.status
      );
      console.log(
        "[AssignmentService.addDegradations] Données reçues:",
        JSON.stringify(response.data)
      );
      return response.data;
    } catch (error) {
      console.error("[AssignmentService.addDegradations] Erreur:", error);
      return handleApiError("Erreur lors de l'ajout des dégradations", error);
    }
  },

  // Mettre à jour la propreté
  updateCleanliness: async (
    id: string,
    cleanlinessData: VehicleState["cleanliness"],
    isReturn = false
  ): Promise<Assignment> => {
    try {
      const url = `${API_URL}/assignments/${id}/cleanliness${
        isReturn ? "?return=true" : ""
      }`;
      console.log(
        "[AssignmentService.updateCleanliness] Requête PATCH vers",
        url
      );
      console.log(
        "[AssignmentService.updateCleanliness] Données envoyées:",
        JSON.stringify(cleanlinessData)
      );
      console.log(
        "[AssignmentService.updateCleanliness] Mode retour:",
        isReturn
      );

      const response = await axios.patch(url, cleanlinessData);

      console.log(
        "[AssignmentService.updateCleanliness] Réponse reçue:",
        response.status
      );
      console.log(
        "[AssignmentService.updateCleanliness] Données reçues:",
        JSON.stringify(response.data)
      );
      return response.data;
    } catch (error) {
      console.error("[AssignmentService.updateCleanliness] Erreur:", error);
      return handleApiError(
        "Erreur lors de la mise à jour de la propreté",
        error
      );
    }
  },

  // Remplir la checklist
  updateChecklist: async (
    id: string,
    checklistData: ChecklistData
  ): Promise<Assignment> => {
    try {
      // Vérifier si nous sommes en mode retour
      const isReturn = checklistData.isReturn === true;
      // Supprimer la propriété isReturn de l'objet avant de l'envoyer à l'API
      if (isReturn) {
        delete checklistData.isReturn;
      }

      const url = `${API_URL}/assignments/${id}/checklist${
        isReturn ? "?return=true" : ""
      }`;

      console.log(
        "[AssignmentService.updateChecklist] Requête PATCH vers",
        url
      );
      console.log(
        "[AssignmentService.updateChecklist] Données envoyées:",
        JSON.stringify(checklistData)
      );
      console.log("[AssignmentService.updateChecklist] Mode retour:", isReturn);

      const response = await axios.patch(url, checklistData);

      console.log(
        "[AssignmentService.updateChecklist] Réponse reçue:",
        response.status
      );
      console.log(
        "[AssignmentService.updateChecklist] Données reçues:",
        JSON.stringify(response.data)
      );

      return response.data;
    } catch (error) {
      console.error("[AssignmentService.updateChecklist] Erreur:", error);
      return handleApiError(
        "Erreur lors de la mise à jour de la checklist",
        error
      );
    }
  },

  /**
   * Ajoute les signatures à une affectation
   * @param id Identifiant de l'affectation
   * @param signaturesData Données des signatures
   * @param isReturn Indique si c'est une signature de retour (true) ou d'entrée (false)
   * @param forceUpdate Force la mise à jour des signatures même si l'étape actuelle n'est pas celle attendue
   * @returns L'affectation mise à jour
   */
  addSignatures: async (
    id: string,
    signaturesData: SignaturesData,
    isReturn = false,
    forceUpdate = false
  ): Promise<Assignment> => {
    try {
      const url = `${API_URL}/assignments/${id}/signatures${
        isReturn ? "?return=true" : ""
      }${forceUpdate ? (isReturn ? "&" : "?") + "force=true" : ""}`;
      console.log("[AssignmentService.addSignatures] Requête PATCH vers", url);
      console.log(
        "[AssignmentService.addSignatures] Données envoyées:",
        JSON.stringify(signaturesData)
      );
      console.log("[AssignmentService.addSignatures] Mode retour:", isReturn);
      console.log(
        "[AssignmentService.addSignatures] Force update:",
        forceUpdate
      );

      const response = await axios.patch(url, signaturesData);

      console.log(
        "[AssignmentService.addSignatures] Réponse reçue:",
        response.status
      );
      console.log(
        "[AssignmentService.addSignatures] Données reçues:",
        JSON.stringify(response.data)
      );
      return response.data;
    } catch (error) {
      console.error("[AssignmentService.addSignatures] Erreur:", error);
      return handleApiError("Erreur lors de l'ajout des signatures", error);
    }
  },

  // Démarrer le processus de retour
  startReturn: async (id: string): Promise<Assignment> => {
    try {
      console.log(
        "[AssignmentService.startReturn] Requête PATCH vers",
        `${API_URL}/assignments/${id}/start-return`
      );
      const response = await axios.patch(
        `${API_URL}/assignments/${id}/start-return`,
        {}
      );
      console.log(
        "[AssignmentService.startReturn] Réponse reçue:",
        response.status
      );
      console.log(
        "[AssignmentService.startReturn] Données reçues:",
        JSON.stringify(response.data)
      );
      return response.data;
    } catch (error) {
      console.error("[AssignmentService.startReturn] Erreur:", error);
      return handleApiError(
        "Erreur lors du démarrage du processus de retour",
        error
      );
    }
  },

  // Terminer l'affectation
  endAssignment: async (
    id: string,
    endData: EndAssignmentData
  ): Promise<Assignment> => {
    try {
      console.log(
        "[AssignmentService.endAssignment] Requête PATCH vers",
        `${API_URL}/assignments/${id}/end`
      );
      console.log(
        "[AssignmentService.endAssignment] Données envoyées:",
        JSON.stringify(endData)
      );
      const response = await axios.patch(
        `${API_URL}/assignments/${id}/end`,
        endData
      );
      console.log(
        "[AssignmentService.endAssignment] Réponse reçue:",
        response.status
      );
      return response.data;
    } catch (error) {
      console.error("[AssignmentService.endAssignment] Erreur:", error);
      return handleApiError("Erreur lors de la fin de l'affectation", error);
    }
  },
};

export default AssignmentService;
